import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { LOGIN_CODE_TTL_MINUTES, type EmailCodeRejection } from '@hv/shared';
import { ApiError } from '../api/client';
import { useRequestEmailCode, useVerifyEmailCode } from '../api/hooks';
import { describeError } from '../components/Notify';

const CODE_REASONS: Record<EmailCodeRejection, string> = {
  code_invalid: 'That code is not right. Check the latest email and try again.',
  code_expired: 'That code has expired. Send yourself a new one.',
  code_used: 'That code has already been used. Send yourself a new one.',
  code_locked: 'Too many wrong attempts for this code. Send yourself a new one.',
  inactive: 'Your account has been deactivated. Contact the Admissions office.',
  method_not_allowed:
    'Your account cannot sign in with an emailed code. Use Google or contact the Admissions office.',
};

function codeError(err: unknown): string {
  if (err instanceof ApiError && err.status === 401)
    return CODE_REASONS[err.message as EmailCodeRejection] ?? err.message;
  if (err instanceof ApiError && err.status === 429) return err.message;
  return describeError(err);
}

export function EmailCodeSignIn({ returnTo, onBack }: { returnTo: string; onBack: () => void }) {
  const navigate = useNavigate();
  const requestCode = useRequestEmailCode();
  const verify = useVerifyEmailCode();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const send = (resend: boolean) => {
    const target = email.trim().toLowerCase();
    requestCode.mutate(target, {
      onSuccess: () => {
        setSentTo(target);
        setResent(resend);
        setCode('');
        verify.reset();
      },
      onError: (err) => console.error('Requesting a sign-in code failed', err),
    });
  };

  const submitEmail = (e: FormEvent) => {
    e.preventDefault();
    send(false);
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (!sentTo) return;
    verify.mutate(
      { email: sentTo, code },
      {
        onSuccess: () => navigate(returnTo, { replace: true }),
        onError: (err) => console.error('Verifying the sign-in code failed', err),
      },
    );
  };

  return (
    <Stack gap={2}>
      <Typography variant="h2">Trustee sign-in</Typography>
      {!sentTo ? (
        <Box component="form" onSubmit={submitEmail} aria-label="Request a sign-in code">
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Enter the email address you were invited with. We will email you a 6-digit sign-in code.
          </Typography>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            sx={{ mb: 2 }}
          />
          {requestCode.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {codeError(requestCode.error)}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={requestCode.isPending || !email.includes('@')}
          >
            Email me a code
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={submitCode} aria-label="Code verification">
          <Alert severity={resent ? 'success' : 'info'} sx={{ mb: 2 }}>
            {resent ? 'A new code is on its way. ' : ''}If {sentTo} is invited to the HV Platform,
            we have emailed it a 6-digit code. The code is valid for {LOGIN_CODE_TTL_MINUTES}{' '}
            minutes and works once.
          </Alert>
          <TextField
            label="Sign-in code"
            required
            fullWidth
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '\\d{6}', maxLength: 6 } }}
            sx={{ mb: 2 }}
          />
          {verify.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {codeError(verify.error)}
            </Alert>
          )}
          {requestCode.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {codeError(requestCode.error)}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={verify.isPending || code.length !== 6}
          >
            Verify code
          </Button>
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Button size="small" onClick={() => send(true)} disabled={requestCode.isPending}>
              Send a new code
            </Button>
            <Button
              size="small"
              onClick={() => {
                setSentTo(null);
                setCode('');
                verify.reset();
                requestCode.reset();
              }}
            >
              Use a different email
            </Button>
          </Stack>
        </Box>
      )}
      <Button variant="text" onClick={onBack}>
        Back to sign-in options
      </Button>
    </Stack>
  );
}
